import React, { useEffect, useState } from 'react';
import { ClickAwayListener, Popper } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { HdButton, HdIcon, HdIconButton } from '../../../../components';
import useSettingsStorageService from '../../../../auth/hooks/useSettingsStorageService';
import { isSettingsRoute } from '../../../../layout/constants/settingsNav';
import {
  SETTINGS_NAV_GUIDE_DELAY,
  SETTINGS_NAV_GUIDE_SHOWN_KEY
} from '../../../constants/settingsNavGuide';
import styles from './SettingsNavGuide.module.scss';

export const dataIds = {
  base: 'settings-nav-guide',
  close: 'settings-nav-guide-close',
  dismiss: 'settings-nav-guide-dismiss',
  later: 'settings-nav-guide-later'
};

interface SettingsNavGuideProps {
  /** CSS selector for the appbar item the popover points at. */
  target: string;
  /** Lets the appbar light that item up while the pointer is open. */
  onOpenChange?: (open: boolean) => void;
}

/**
 * One-time pointer to where Account, Team, Billing and Alerts went. "Got it" writes the flag
 * through SettingsStorageService, which keys local storage per user — so dismissing it on a
 * shared browser does not silence it for the next person.
 */
export function SettingsNavGuide({ target, onOpenChange }: SettingsNavGuideProps) {
  const { getSettings, applySettings } = useSettingsStorageService();
  const { pathname } = useLocation();
  /*
   * The anchor element is the open flag: resolved from the DOM once the appbar has painted,
   * so the popper always has something to measure. The toolkit's menu popper was doing that
   * resolution during its own render and parking the card away from the item.
   */
  const [anchorEl, setAnchorEl] = useState<Element | null>(null);
  const [caretEl, setCaretEl] = useState<HTMLElement | null>(null);

  const isOnSettingsRoute = isSettingsRoute(pathname);

  useEffect(() => {
    // Already dismissed, or the user is in Settings and has plainly found it.
    if (getSettings(SETTINGS_NAV_GUIDE_SHOWN_KEY) || isOnSettingsRoute) {
      return undefined;
    }

    const timer = window.setTimeout(
      () => setAnchorEl(document.querySelector(target)),
      SETTINGS_NAV_GUIDE_DELAY
    );

    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    onOpenChange?.(!!anchorEl);
  }, [anchorEl]);

  const close = () => setAnchorEl(null);

  const dismissForGood = () => {
    applySettings(SETTINGS_NAV_GUIDE_SHOWN_KEY, true);
    close();
  };

  if (!anchorEl) {
    return null;
  }

  return (
    <Popper
      open
      anchorEl={anchorEl}
      placement='right'
      className={styles.popper}
      modifiers={[
        { name: 'offset', options: { offset: [0, 12] } },
        // Popper places the caret itself, so it keeps pointing at the item even if the
        // card shifts to stay inside the viewport.
        { name: 'arrow', options: { element: caretEl, padding: 12 } }
      ]}
    >
      <ClickAwayListener onClickAway={close}>
        <div className={styles.guide} data-id={dataIds.base}>
          <span ref={setCaretEl} className={styles.caret} />

          <div className={styles.header}>
            <span className='text-label-small'>Settings moved here</span>

            <HdIconButton dataId={dataIds.close} onClick={close} aria-label='Close'>
              <HdIcon name='close' size={2} />
            </HdIconButton>
          </div>

          <p className={styles.body}>
            Account, Team, Billing and Alerts have left the profile menu. They now live together
            under Settings.
          </p>

          <div className={styles.actions}>
            <HdButton variation='outline' size='sm' dataId={dataIds.later} onClick={close}>
              Remind me later
            </HdButton>

            <HdButton size='sm' dataId={dataIds.dismiss} onClick={dismissForGood}>
              Got it
            </HdButton>
          </div>
        </div>
      </ClickAwayListener>
    </Popper>
  );
}
